import React, { useMemo, useState } from 'react';
import { PUBLICATIONS, OPEN_SOURCE } from './consts';
import { Publication } from './types';
import './styles.scss';

const formatAuthors = (authors: string[]): string => {
  if (authors.length <= 2) return authors.join(' & ');
  return `${authors.slice(0, -1).join(', ')} & ${authors[authors.length - 1]}`;
};

const PublicationsPage: React.FC = () => {
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const tags = useMemo(() => {
    const all = PUBLICATIONS.flatMap(p => p.tags ?? []);
    return Array.from(new Set(all)).sort();
  }, []);

  const visible = useMemo(
    () => (activeTag ? PUBLICATIONS.filter(p => p.tags?.includes(activeTag)) : PUBLICATIONS),
    [activeTag]
  );

  // Group by year, newest first.
  const byYear = useMemo(() => {
    const groups = new Map<number, Publication[]>();
    visible.forEach(p => {
      const bucket = groups.get(p.year) ?? [];
      bucket.push(p);
      groups.set(p.year, bucket);
    });
    return Array.from(groups.entries()).sort((a, b) => b[0] - a[0]);
  }, [visible]);

  return (
    <div className="page-container">
      <div className="pubs-hero">
        <h1>Publications</h1>
        <p className="pubs-subtitle">
          Peer-reviewed research and preprints behind LLEO&apos;s satellite analysis methods.
        </p>
      </div>

      {/* ---- Open-source release ---- */}
      <section className="oss-section" aria-labelledby="oss-title">
        <div className="oss-card">
          <div className="oss-head">
            <span className="mono-tag oss-kicker">Open source</span>
            <h2 id="oss-title" className="oss-title">
              {OPEN_SOURCE.name}
              <span className="oss-title-sub"> — the agentic Earth-observation core of LLEO</span>
            </h2>
          </div>

          <p className="oss-summary">{OPEN_SOURCE.summary}</p>

          <ul className="oss-tools" aria-label="Tools in the repository">
            {OPEN_SOURCE.tools.map(t => (
              <li className="oss-tool" key={t.name}>
                <code className="oss-tool-name">{t.name}</code>
                <span className="oss-tool-source">{t.source}</span>
                <span className="oss-tool-what">{t.what}</span>
              </li>
            ))}
          </ul>

          <div className="oss-links">
            <a className="btn btn-primary oss-btn" href={OPEN_SOURCE.repoUrl} target="_blank" rel="noopener noreferrer">
              View on GitHub ↗
            </a>
            <a className="pub-link" href={OPEN_SOURCE.issuesUrl} target="_blank" rel="noopener noreferrer">
              Issues
            </a>
            <a className="pub-link" href={OPEN_SOURCE.paperReadmeUrl} target="_blank" rel="noopener noreferrer">
              Paper pipeline
            </a>
          </div>

          <h3 className="oss-contrib-title">How to contribute</h3>
          <ol className="oss-contrib">
            {OPEN_SOURCE.contribute.map(c => (
              <li className="oss-contrib-item" key={c.title}>
                <strong>{c.title}</strong>
                <span>{c.text}</span>
              </li>
            ))}
          </ol>
          <p className="oss-foot">
            Clone the repo, add your Earth Engine service-account JSON and API keys to <code>.env</code>, run{' '}
            <code>pip install -r requirements.txt</code>, then <code>python cli.py ndvi "Central Park, New York" 2023 2024</code>{' '}
            to verify your setup before opening a pull request.
          </p>
        </div>
      </section>

      {PUBLICATIONS.length === 0 ? (
        <div className="pubs-empty">
          <div className="pubs-empty-icon" aria-hidden="true">📄</div>
          <h2>No publications listed yet</h2>
          <p>
            Papers will appear here as they are published. The list is grouped by
            year, with links to the DOI, PDF and code for each entry.
          </p>
        </div>
      ) : (
        <>
          {tags.length > 0 && (
            <div className="pubs-filters" role="group" aria-label="Filter by topic">
              <button
                className={`pubs-filter ${activeTag === null ? 'active' : ''}`}
                onClick={() => setActiveTag(null)}
                aria-pressed={activeTag === null}
              >
                All
              </button>
              {tags.map(tag => (
                <button
                  key={tag}
                  className={`pubs-filter ${activeTag === tag ? 'active' : ''}`}
                  onClick={() => setActiveTag(tag)}
                  aria-pressed={activeTag === tag}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {byYear.map(([year, items]) => (
            <section className="pubs-year-group" key={year}>
              <h2 className="pubs-year">{year}</h2>
              <ul className="pubs-list">
                {items.map(pub => (
                  <li className="pub-card" key={`${pub.year}-${pub.title}`}>
                    <h3 className="pub-title">{pub.title}</h3>
                    <p className="pub-authors">{formatAuthors(pub.authors)}</p>
                    <p className="pub-venue">
                      {pub.venue} · {pub.year}
                    </p>

                    {pub.abstract && <p className="pub-abstract">{pub.abstract}</p>}

                    {pub.tags && pub.tags.length > 0 && (
                      <ul className="pub-tags">
                        {pub.tags.map(tag => (
                          <li className="pub-tag" key={tag}>
                            {tag}
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="pub-links">
                      {pub.doi && (
                        <a className="pub-link" href={pub.doi} target="_blank" rel="noopener noreferrer">
                          DOI
                        </a>
                      )}
                      {pub.pdfUrl && (
                        <a className="pub-link" href={pub.pdfUrl} target="_blank" rel="noopener noreferrer">
                          PDF
                        </a>
                      )}
                      {pub.codeUrl && (
                        <a className="pub-link" href={pub.codeUrl} target="_blank" rel="noopener noreferrer">
                          Code
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </div>
  );
};

export default PublicationsPage;
